import { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { showInkToast } from '../cms/InkToast';

/**
 * Technical support (apiserver platform/services/supportAccess.js; Prototype
 * "Team", 2026-10-21). TinyCMS staff ask for access from the ops portal; the
 * owner approves for 24 hours, 7 days or 30 days, or declines, at the top of
 * Settings → Team. Approved, the person shows in Team as Technical support (no
 * seat) until their access ends on its own or the owner ends it.
 *
 * Technical support is also the only role that sees Prism AI's extracted
 * detail (facts and entities): useSeesExtractedDetail. The API returns none
 * to anyone else, so the screens hide those sections rather than show them empty.
 */

export interface SupportAccessRequest {
  id: string;
  requesterEmail: string;
  requesterName: string | null;
  ticket: string | null;
  reason: string;
  status: string;
  accessDays: number | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface SupportAccessView {
  canDecide: boolean;
  ownerEmail: string | null;
  canDo: string;
  pending: SupportAccessRequest[];
  active: SupportAccessRequest[];
}

const FIELDS = `id requesterEmail requesterName ticket reason status accessDays expiresAt createdAt`;

export const GET_SUPPORT_ACCESS = gql`
  query SupportAccess($accountId: ID!) {
    supportAccess(accountId: $accountId) {
      canDecide ownerEmail canDo
      pending { ${FIELDS} }
      active { ${FIELDS} }
    }
  }
`;

export const DECIDE_SUPPORT_ACCESS = gql`
  mutation DecideSupportAccess($accountId: ID!, $id: ID!, $approve: Boolean!, $days: Int) {
    decideSupportAccess(accountId: $accountId, id: $id, approve: $approve, days: $days) { ${FIELDS} }
  }
`;

export const END_SUPPORT_ACCESS = gql`
  mutation EndSupportAccess($accountId: ID!, $id: ID!) {
    endSupportAccess(accountId: $accountId, id: $id) { ${FIELDS} }
  }
`;

export const SEES_EXTRACTED_DETAIL = gql`
  query SeesExtractedDetail($accountId: ID!) {
    seesExtractedDetail(accountId: $accountId)
  }
`;

export function useSupportAccess(
  accountId: string | null | undefined,
  { poll = false }: { poll?: boolean } = {}
) {
  const { data, refetch } = useQuery<{ supportAccess: SupportAccessView }>(GET_SUPPORT_ACCESS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
    // The Approvals review's count includes these (owner only): refresh with it.
    pollInterval: poll ? 60_000 : undefined,
  });
  return { view: data?.supportAccess ?? null, refetch };
}

/**
 * Whether this person sees facts and entities on this site (Technical support
 * only). False while loading or on any error: hidden is the safe default.
 */
export function useSeesExtractedDetail(accountId: string | null | undefined): boolean {
  const { data } = useQuery<{ seesExtractedDetail: boolean }>(SEES_EXTRACTED_DETAIL, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  return Boolean(data?.seesExtractedDetail);
}

/** "Alex Kim" (or the email's name part when staff gave no name). */
export const supportName = (r: Pick<SupportAccessRequest, 'requesterName' | 'requesterEmail'>) =>
  r.requesterName || r.requesterEmail.split('@')[0];

/** "AK" for Alex Kim; "AL" for alex.kim with no name. */
export const supportInitials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (words.length > 1 ? words[0][0] + words[1][0] : name.slice(0, 2)).toUpperCase();
};

/** "Oct 17". */
export const shortDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

/** "today at 2:12 PM" / "Oct 15 at 9:04 AM". */
export function askedLabel(iso: string, now = new Date()) {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return d.toDateString() === now.toDateString()
    ? `today at ${time}`
    : `${shortDate(iso)} at ${time}`;
}

export const ACCESS_SPANS: { days: number; label: string }[] = [
  { days: 1, label: '24 hours' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
];

/** When access would end if given now for `days`. */
export const endsOn = (days: number, now = new Date()) =>
  shortDate(new Date(now.getTime() + days * 86400000).toISOString());

/**
 * The open requests, at the top of Team: who, the ticket, why, when — and, for
 * the owner, Decline and Review access (the dialog picks how long).
 */
export function SupportRequests({
  accountId,
  view,
  onChanged,
}: {
  accountId: string;
  view: SupportAccessView;
  onChanged: () => void;
}) {
  const [reviewing, setReviewing] = useState<SupportAccessRequest | null>(null);
  const [days, setDays] = useState(7);
  const [decide, { loading }] = useMutation(DECIDE_SUPPORT_ACCESS);

  const answer = async (r: SupportAccessRequest, approve: boolean) => {
    try {
      await decide({ variables: { accountId, id: r.id, approve, days: approve ? days : null } });
      showInkToast(
        approve
          ? `${supportName(r)} from tinycms support has access until ${endsOn(days)}.`
          : `Declined. ${supportName(r)} from tinycms support has no access.`
      );
      setReviewing(null);
      onChanged();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  if (!view.pending.length) return null;
  return (
    <>
      {view.pending.map((r) => (
        <div className="ink-support-ask" key={r.id}>
          <span className="ink-team-avatar" data-tone="soft">
            {supportInitials(supportName(r))}
          </span>
          <div className="ink-support-ask-text">
            <div className="ink-team-name">tinycms technical support is asking for access</div>
            <div className="ink-team-email">
              {supportName(r)} ({r.requesterEmail}){r.ticket ? `, for ticket ${r.ticket}` : ''}, “
              {r.reason}”. Asked {askedLabel(r.createdAt)}.
            </div>
          </div>
          {view.canDecide ? (
            <span className="ink-support-ask-actions">
              <button
                type="button"
                className="ink-btn"
                disabled={loading}
                onClick={() => answer(r, false)}
              >
                Decline
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => {
                  setDays(7);
                  setReviewing(r);
                }}
              >
                Review access
              </button>
            </span>
          ) : (
            <span className="ink-team-email">
              {view.ownerEmail
                ? `Only the owner, ${view.ownerEmail}, can decide.`
                : 'Waiting for an admin.'}
            </span>
          )}
        </div>
      ))}

      <Dialog open={Boolean(reviewing)} onOpenChange={(open) => !open && setReviewing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Give {reviewing ? supportName(reviewing) : ''} from tinycms support access?
            </DialogTitle>
            <DialogDescription>{view.canDo}</DialogDescription>
          </DialogHeader>
          <div>
            <div className="ink-team-email" style={{ marginBottom: 8 }}>
              Access for
            </div>
            <div className="ink-seg" role="radiogroup" aria-label="Access for">
              {ACCESS_SPANS.map((s) => (
                <button
                  key={s.days}
                  type="button"
                  role="radio"
                  aria-checked={days === s.days}
                  data-active={days === s.days}
                  onClick={() => setDays(s.days)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="ink-team-email" style={{ marginTop: 10 }}>
              Access ends {endsOn(days)} on its own. You can end it sooner in Team.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewing(null)} disabled={loading}>
              Cancel
            </Button>
            <Button
              className="bg-button-primary text-button-primary"
              disabled={loading}
              onClick={() => reviewing && answer(reviewing, true)}
            >
              {loading ? 'Giving access…' : 'Give access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
